import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text, Spinner, SpinnerSize, Dropdown, IDropdownOption,
  ComboBox, IComboBoxOption,
  Toggle, TooltipHost, Panel, PanelType,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  ComposedChart, Area,
  BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  CaretLeft, CaretRight, Clock, ArrowsClockwise, CurrencyDollar,
  ChartBar, Funnel, ShoppingCart, TrendUp, Briefcase,
  HouseLine,
} from '@phosphor-icons/react';
import { boroondaraPalette } from '../theme/boroondaraTheme';
import { accessService } from '../services/accessService';
import AccessSummaryBanner from '../components/common/AccessSummaryBanner';
import DataTable, { Column } from '../components/common/DataTable';
import FinancialOverview from '../components/finance/FinancialOverview';

// ── Palette & constants ──
const p = boroondaraPalette;
const GREEN = '#2e873d';
const RED = '#D32F2F';

const GREEN_PALETTE = ['#2e873d', '#55a655', '#7BC47B', '#A8DCA8', '#D4F0D4', '#1a5c26', '#3d9e4d', '#6db86d'];
const BLUE_PALETTE  = ['#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#1D4ED8', '#1E40AF', '#7DD3FC'];
const SUNSET_PALETTE= ['#E76F51', '#F4A261', '#E9C46A', '#2A9D8F', '#264653', '#D4623C', '#F08C4A', '#C94C30'];
// DONUT_PALETTE removed — Overview redesign replaced the donut with a
// circular progress meter in FinancialOverview.tsx.

// Australian FY period list used by the Overview "YTD to period" dropdown.
// Periods are Oracle-style: 1=Jul, 2=Aug, …, 12=Jun.
const PERIOD_OPTIONS: IDropdownOption[] = [
  { key: '',   text: 'Full Year'  },
  { key: '1',  text: 'Jul (P1)'  },
  { key: '2',  text: 'Aug (P2)'  },
  { key: '3',  text: 'Sep (P3)'  },
  { key: '4',  text: 'Oct (P4)'  },
  { key: '5',  text: 'Nov (P5)'  },
  { key: '6',  text: 'Dec (P6)'  },
  { key: '7',  text: 'Jan (P7)'  },
  { key: '8',  text: 'Feb (P8)'  },
  { key: '9',  text: 'Mar (P9)'  },
  { key: '10', text: 'Apr (P10)' },
  { key: '11', text: 'May (P11)' },
  { key: '12', text: 'Jun (P12)' },
];

const TAB_CONFIG = [
  { id: 'overview',       label: 'Financial Overview', icon: CurrencyDollar, color: GREEN,     palette: GREEN_PALETTE },
  { id: 'variance',       label: 'Budget vs Actual',   icon: TrendUp,        color: '#7C7CFF', palette: GREEN_PALETTE },
  { id: 'costcentres',    label: 'Hierarchy',          icon: ChartBar,       color: '#2A9D8F', palette: GREEN_PALETTE },
  { id: 'projects',       label: 'Projects',           icon: Briefcase,      color: '#E76F51', palette: SUNSET_PALETTE },
  { id: 'purchaseorders', label: 'Purchase Orders',    icon: ShoppingCart,   color: '#2563EB', palette: BLUE_PALETTE },
];

// Filters per tab — hierarchy (directorate/department/costCentre) is available on every tab that drills into CC
const TAB_FILTERS: Record<string, string[]> = {
  overview:       ['fy', 'period', 'directorate', 'department', 'costCentre', 'excludeProject'],
  variance:       ['fy', 'directorate', 'department', 'costCentre', 'excludeProject'],
  costcentres:    ['fy', 'directorate', 'department', 'excludeProject'],
  projects:       ['fy', 'directorate', 'department', 'costCentre'],
  purchaseorders: ['directorate', 'department', 'costCentre', 'poStatus', 'excludeProject'],
};

const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });
const tooltipStyle = { backgroundColor: '#FFFFFF', border: '1px solid #E1E1E1', borderRadius: 4, color: '#232323', fontSize: 12 };

// ── Helpers ──
function fmtCurrency(value: number): string {
  if (Math.abs(value) >= 1_000_000) return '$' + (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000) return '$' + (value / 1_000).toFixed(0) + 'K';
  return '$' + value.toLocaleString('en-AU', { maximumFractionDigits: 0 });
}
function fmtAxis(value: number): string {
  if (Math.abs(value) >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(value) >= 1_000) return (value / 1_000).toFixed(0) + 'K';
  return String(value);
}
function truncLabel(label: string, max = 20): string {
  return label && label.length > max ? label.slice(0, max) + '...' : (label || '');
}
// fmtTableCurrency removed — table formatting now lives inside DataTable.tsx.

// ── KPI hero value — mirrors HeroValue in FinancialOverview for visual consistency ──
// Renders: styled $ prefix + big tabular number + smaller unit suffix (M / k)
// Percentage variant: big number + smaller % suffix
// Count variant (format: 'count'): plain integer, no $ prefix, no unit abbreviation
function KpiHeroValue({ kpi }: { kpi: { raw: number; suffix?: string; format?: string } }) {
  const n = Number(kpi.raw) || 0;
  const T1 = '#232323', T3 = '#747474', RED = '#D32F2F';
  if (kpi.suffix === '%') {
    return (
      <div style={{ fontSize: 40, fontWeight: 800, color: T1, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {Math.abs(n).toFixed(1)}<span style={{ fontSize: 24, color: T3, marginLeft: 1 }}>%</span>
      </div>
    );
  }
  if (kpi.format === 'count') {
    return (
      <div style={{ fontSize: 40, fontWeight: 800, color: T1, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
        {Math.round(Math.abs(n)).toLocaleString('en-AU')}
      </div>
    );
  }
  const isNeg = n < 0;
  const abs = Math.abs(n);
  let val: string, unit: string;
  if (abs >= 1_000_000) { val = (abs / 1_000_000).toFixed(1); unit = 'M'; }
  else if (abs >= 1_000) { val = (abs / 1_000).toFixed(0);   unit = 'k'; }
  else { val = String(Math.round(abs)); unit = ''; }
  return (
    <div style={{ fontSize: 40, fontWeight: 800, color: isNeg ? RED : T1, letterSpacing: '-0.025em', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
      {isNeg && <span style={{ fontSize: 28, marginRight: 1 }}>−</span>}
      <span style={{ fontSize: 20, fontWeight: 600, color: T3, marginRight: 1, verticalAlign: 'top', letterSpacing: 0 }}>$</span>
      {val}
      {unit && <span style={{ fontSize: 24, color: T3, marginLeft: 2 }}>{unit}</span>}
    </div>
  );
}

// ── Custom bar value label — mirrors rollupValueLabel in FinancialOverview ──
function BarValueLabel({ x, y, width, height, value }: any) {
  if (!isFinite(value) || value === 0) return null;
  return (
    <text
      x={(x as number) + (width as number) + 8}
      y={(y as number) + (height as number) / 2}
      dominantBaseline="central"
      style={{ fontSize: 11, fontFamily: 'Segoe UI, system-ui, sans-serif', fontVariantNumeric: 'tabular-nums', fill: '#484848', fontWeight: 600 }}
    >
      {fmtAxis(value)}
    </text>
  );
}

// ── Styles ──
// Chart cards and KPI cards use var(--tc) for their accent colour.
// Set --tc on the container div each render to match the active tab's colour
// so every card automatically inherits the right accent without per-card style props.
const s = mergeStyleSets({
  page: { padding: '28px 24px', minHeight: 'calc(100vh - 52px)', background: '#F3F4F6', '@media (max-width: 768px)': { padding: 16 } } as any,
  container: { maxWidth: 1280, margin: '0 auto' },
  headerRow: { marginBottom: 16 },
  title: { color: p.text, fontWeight: 800 as any, letterSpacing: '-0.025em' },
  subtitle: { color: p.text2, marginTop: 5, maxWidth: 740, lineHeight: '1.65', fontSize: 14 },
  timestamp: { color: p.text3, fontSize: 12, marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const },

  // ── Tab strip ──
  tabStrip: {
    display: 'flex', gap: 0, marginBottom: 20, flexWrap: 'wrap' as const,
    background: '#FFFFFF', border: '1px solid #E4E4E4', borderRadius: 8,
    padding: 4, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  tab: {
    padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600 as any,
    border: 'none', background: 'none', color: '#747474', cursor: 'pointer',
    transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
    selectors: { ':hover': { background: '#F5F5F5', color: '#232323' } },
  } as any,

  // ── Filter bar ──
  filterSection: { marginBottom: 16 },
  filterBar: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' as const },
  filterLabel: {
    fontSize: 11, fontWeight: 700 as any, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
    display: 'flex', alignItems: 'center', gap: 4, marginRight: 2, whiteSpace: 'nowrap' as const,
  },

  // ── Active-filter chips (shown below the filter bar) ──
  chipRow: {
    display: 'flex', alignItems: 'center', gap: 6, minHeight: 24,
    flexWrap: 'wrap' as const, marginTop: 2,
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 0,
    background: '#E8F5E9', border: '1px solid rgba(46,135,61,0.30)',
    borderRadius: 20, fontSize: 12, fontWeight: 500 as any,
    color: GREEN, overflow: 'hidden',
  },
  chipLabel: { padding: '3px 8px 3px 10px', lineHeight: '1.4' } as any,
  chipClear: {
    padding: '3px 8px 3px 4px', background: 'none', border: 'none',
    cursor: 'pointer', color: GREEN, fontSize: 14, lineHeight: '1',
    display: 'flex', alignItems: 'center',
    selectors: { ':hover': { background: 'rgba(46,135,61,0.12)' } },
  } as any,
  clearAllLink: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 11,
    color: '#9CA3AF', fontWeight: 500 as any, fontFamily: 'inherit',
    padding: '2px 4px', textDecoration: 'underline', marginLeft: 2,
    selectors: { ':hover': { color: '#484848' } },
  } as any,

  // ── Context eyebrow strip (non-overview tabs, mirrors fo-context in FinancialOverview) ──
  tabContext: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const,
    padding: '11px 16px', border: '1px solid #E4E4E4', borderRadius: 6,
    marginBottom: 16, fontSize: 10.5, fontWeight: 700 as any,
    letterSpacing: '0.13em', textTransform: 'uppercase' as const, color: '#484848',
  },
  tabCtxSep: { color: '#9aa0a6', fontWeight: 400 as any, letterSpacing: 0 },
  tabCtxPill: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '2px 8px', background: '#FFFFFF', border: '1px solid #E1E1E1',
    borderRadius: 10, color: '#484848', letterSpacing: '0.06em', fontSize: 10,
  },

  // ── KPI strip ──
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 24, '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 } } as any,
  kpiCard: {
    // Top accent stripe matches chart cards and Overview hero tiles — consistent card language
    padding: '20px 22px 18px', borderRadius: 8, background: '#FFFFFF',
    border: '1px solid #E4E4E4', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderTop: '3px solid var(--tc, #2e873d)',
  },
  kpiLabel: { color: p.text3, fontSize: 10, letterSpacing: '0.10em', textTransform: 'uppercase' as any, fontWeight: 700 as any, marginBottom: 10 },
  kpiValue: { color: p.text, fontWeight: 800 as any, fontSize: 32, lineHeight: '38px', letterSpacing: '-0.03em', marginBottom: 3, fontVariantNumeric: 'tabular-nums', '@media (max-width: 768px)': { fontSize: 24, lineHeight: '30px' } } as any,
  kpiHint: { color: p.text3, fontSize: 11, marginTop: 2 },

  // ── Charts ──
  chartsRow: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 16, '@media (max-width: 768px)': { gridTemplateColumns: '1fr' } } as any,
  chartsRowFull: { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 16 },
  chartCard: {
    padding: '18px 22px 22px', borderRadius: 8, background: '#FFFFFF',
    border: '1px solid #E4E4E4', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderTop: '3px solid var(--tc, #2e873d)',
    '@media (max-width: 768px)': { padding: '14px 14px 16px' },
  } as any,
  chartTitle: {
    color: p.text, fontWeight: 700 as any, fontSize: 13.5, marginBottom: 16,
    display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' as const,
    letterSpacing: '-0.01em',
  },
  chartTitleInner: { display: 'flex', alignItems: 'center', gap: 7 } as any,

  // ── Section dividers (editorial style matching FinancialOverview) ──
  sectionHead: { display: 'flex', alignItems: 'center', gap: 12, margin: '28px 0 10px' },
  sectionTitle: {
    fontSize: 11, fontWeight: 700 as any, letterSpacing: '0.16em',
    textTransform: 'uppercase' as const, color: '#484848', whiteSpace: 'nowrap' as const,
  },
  sectionRule: { flex: 1, height: 1, background: '#E4E4E4' },
  sectionHint: {
    fontSize: 11, color: '#747474', fontWeight: 500 as any,
    letterSpacing: '0.04em', whiteSpace: 'nowrap' as const,
  },

  // ── Chart card header (two-column: title left, inline legend/meta right) ──
  chartCardHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between' as const,
    marginBottom: 14,
  },
  chartCardTitle: {
    fontSize: 14, fontWeight: 700 as any, color: '#232323', letterSpacing: '-0.005em',
  },
  chartCardMeta: {
    display: 'flex', gap: 14, alignItems: 'center', fontSize: 11, color: '#747474', flexWrap: 'wrap' as const,
  },

  // ── Table section ──
  tableCard: {
    padding: '18px 22px', borderRadius: 8, background: '#FFFFFF',
    border: '1px solid #E4E4E4', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 16,
  },
  tableTitle: { color: p.text, fontWeight: 700 as any, fontSize: 13.5, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 },

  // ── Drill breadcrumb ──
  breadcrumbRow: {
    display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
    color: p.text3, flexWrap: 'wrap' as const, marginBottom: 12,
    padding: '6px 12px', background: '#FAFAFA', borderRadius: 6,
    border: '1px solid #EBEBEB',
  },
  breadcrumbChip: { color: p.primary, cursor: 'pointer', background: 'none', border: 'none', padding: 0, fontWeight: 600 as any, fontSize: 12, fontFamily: 'inherit' },
  breadcrumbCurrent: { color: p.text, fontWeight: 600 as any },

  // ── Misc ──
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 12 },
  th: { background: '#F5F5F5', padding: '10px 12px', textAlign: 'left' as const, fontWeight: 600, color: '#484848', borderBottom: '2px solid #E1E1E1', whiteSpace: 'nowrap' as const, position: 'sticky' as const, top: 0, zIndex: 1 },
  td: { padding: '8px 12px', borderBottom: '1px solid #F0F0F0', color: '#232323' },
  trStriped: { background: '#FAFAFA' },
  centerWrap: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 },
  noData: { color: p.text3, fontSize: 13, textAlign: 'center' as any, padding: 40 },
  refreshBtn: {
    marginLeft: 4, color: p.primary, fontSize: 12, fontWeight: 600 as any, height: 28, padding: '0 10px', borderRadius: 6,
    border: `1px solid ${p.borderSoft}`, background: '#FFFFFF', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'all 0.15s ease',
    selectors: { ':hover': { background: '#F0F8F1', borderColor: GREEN }, ':disabled': { opacity: 0.55, cursor: 'default' } },
  } as any,
  spinning: { animationName: spinAnim, animationDuration: '1s', animationIterationCount: 'infinite', animationTimingFunction: 'linear', display: 'inline-block' },
  error: { marginBottom: 16 },
});

// ── Types ──
interface KpiEntry { label: string; raw: number; suffix?: string; detail?: string; hint?: string; format?: string }
interface SummaryData {
  kpis?: Record<string, KpiEntry>;
  charts?: Record<string, any>;
  table?: any[];
  filters?: {
    financialYears?: number[];
    periods?: Array<{ num: number; name: string }>;
    directorates?: string[];
    departments?: string[];
    costCentres?: string[];
    poStatuses?: string[];
  };
}

// ══════════════════════════════════════════════════════════
// Component
// ══════════════════════════════════════════════════════════
export default function FinanceDashboard() {
  const nav = useNavigate();

  // ── Access control ──
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get('/me');
        if (!alive) return;
        const d = res.data;
        if (d.role === 'admin') setHasAccess(true);
        // allowedDashboards is the authoritative check; fall back to allowedAgents
        // for sessions cached before the allowedDashboards field was added.
        else if (Array.isArray(d.allowedDashboards))
          setHasAccess(d.allowedDashboards.includes('finance_contract_expenditure'));
        else setHasAccess((d.allowedAgents || []).includes('finance'));
      } catch {}
      if (alive) setAccessChecked(true);
    })();
    return () => { alive = false; };
  }, []);

  // ── State ──
  const [activeTab, setActiveTab] = useState('overview');

  // sharedFilters: FY and Period persist across all tab switches.
  // Initialised to the current financial year so the first render matches
  // the backend default without waiting for the options fetch to complete.
  const [sharedFilters, setSharedFilters] = useState<{ fy: string; period: string }>({
    fy: String(new Date().getMonth() >= 6
          ? new Date().getFullYear() + 1
          : new Date().getFullYear()),
    period: '',
  });

  // Per-tab filters: directorate / department / costCentre / poStatus / excludeProject.
  // These reset when the user navigates away (intentional — each tab drills independently).
  const [tabFilters, setTabFilters] = useState<Record<string, Record<string, string>>>({});

  // Pre-loaded dropdown options — populated from /api/finance/options before
  // the heavy /summary data arrives, so dropdowns are usable immediately.
  const [filterOpts, setFilterOpts] = useState<{
    financialYears?: number[];
    directorates?: string[];
    departments?: string[];
    costCentres?: string[];
  } | null>(null);

  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState('');

  // ── Account breakdown drawer (level 1 — account list) ──
  const [breakdown, setBreakdown] = useState<{ name: string; level: string } | null>(null);
  const [breakdownData, setBreakdownData] = useState<{
    summary: { budget: number; actuals: number; variance: number };
    rows: any[];
  } | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  // ── Journal lines drill (level 2 — individual GL postings for one account) ──
  const [accountDrill, setAccountDrill] = useState<{ number: string; name: string } | null>(null);
  const [accountLines, setAccountLines] = useState<any[] | null>(null);
  const [accountLinesCapped, setAccountLinesCapped] = useState(false);
  const [accountLinesLoading, setAccountLinesLoading] = useState(false);

  // currentTabFilters: memoised so the effect dependency array stays stable
  const currentTabFilters = useMemo(() => tabFilters[activeTab] || {}, [tabFilters, activeTab]);

  // Setters
  const setTabFilter = (key: string, value: string) => {
    setTabFilters(prev => {
      const next = { ...(prev[activeTab] || {}), [key]: value };
      // Clear deeper hierarchy filters when parent changes
      if (key === 'directorate') { delete next.department; delete next.costCentre; }
      if (key === 'department') delete next.costCentre;
      return { ...prev, [activeTab]: next };
    });
  };
  const clearTabFilter = (key: string) => setTabFilter(key, '');
  // Changing FY resets period (period numbers are valid for any year, but the
  // intent is usually "YTD to now", so start fresh on FY change)
  const setFY     = (fy: string)     => setSharedFilters(prev => ({ ...prev, fy, period: '' }));
  const setPeriod = (period: string) => setSharedFilters(prev => ({ ...prev, period }));

  // ── Active filter chips (computed at hook level — can't be after early returns) ──
  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onClear: () => void }> = [];
    if (sharedFilters.period) {
      const pLabel = PERIOD_OPTIONS.find(o => o.key === sharedFilters.period)?.text || sharedFilters.period;
      chips.push({ key: 'period', label: `YTD to ${pLabel}`, onClear: () => setPeriod('') });
    }
    if (currentTabFilters.directorate) chips.push({ key: 'dir',  label: currentTabFilters.directorate, onClear: () => clearTabFilter('directorate') });
    if (currentTabFilters.department)  chips.push({ key: 'dept', label: currentTabFilters.department,  onClear: () => clearTabFilter('department') });
    if (currentTabFilters.costCentre)  chips.push({ key: 'cc',   label: currentTabFilters.costCentre,  onClear: () => clearTabFilter('costCentre') });
    if (currentTabFilters.poStatus)    chips.push({ key: 'pos',  label: `Status: ${currentTabFilters.poStatus}`, onClear: () => clearTabFilter('poStatus') });
    if (currentTabFilters.excludeProject === 'true') chips.push({ key: 'ep', label: 'Operating only', onClear: () => clearTabFilter('excludeProject') });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedFilters.period, currentTabFilters]);

  const clearAllFilters = useCallback(() => {
    setPeriod('');
    setTabFilters(prev => ({ ...prev, [activeTab]: {} }));
  }, [activeTab]);

  // Drills from the account list into individual GL journal lines for one account.
  const openAccountDrill = useCallback(async (accountNumber: string, accountName: string) => {
    setAccountDrill({ number: accountNumber, name: accountName });
    setAccountLines(null);
    setAccountLinesCapped(false);
    setAccountLinesLoading(true);
    try {
      const params = new URLSearchParams({
        accountNumber,
        name:  breakdown?.name  || '',
        level: breakdown?.level || 'Directorate',
        fy:    sharedFilters.fy,
      });
      const res = await accessService.get(`/finance/account-lines?${params}`);
      setAccountLines(res.data.rows);
      setAccountLinesCapped(res.data.capped || false);
    } catch {
      setAccountLines([]);
    } finally {
      setAccountLinesLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakdown, sharedFilters.fy]);

  // Opens the account breakdown drawer for the clicked directorate / department / cost centre.
  const openBreakdown = useCallback(async (name: string, level: string) => {
    setBreakdown({ name, level });
    setBreakdownData(null);
    setBreakdownLoading(true);
    try {
      const params = new URLSearchParams({ name, level, fy: sharedFilters.fy });
      const res = await accessService.get(`/finance/cc-breakdown?${params}`);
      setBreakdownData(res.data);
    } catch {
      setBreakdownData({ summary: { budget: 0, actuals: 0, variance: 0 }, rows: [] });
    } finally {
      setBreakdownLoading(false);
    }
  }, [sharedFilters.fy]);

  // ── Pre-load filter options (fast — no fact-table aggregations) ──
  // Called on mount and whenever FY, directorate, or department changes.
  // Populates filterOpts so dropdowns show real values before the heavy
  // summary query completes.
  const fetchOptions = useCallback(async (fy: string, directorate: string, department: string) => {
    try {
      const params = new URLSearchParams();
      if (fy) params.set('fy', fy);
      if (directorate) params.set('directorate', directorate);
      if (department) params.set('department', department);
      const res = await accessService.get(`/finance/options?${params}`);
      setFilterOpts(res.data);
    } catch { /* silently fall back to data?.filters */ }
  }, []);

  useEffect(() => {
    if (accessChecked && hasAccess) {
      fetchOptions(
        sharedFilters.fy,
        currentTabFilters.directorate || '',
        currentTabFilters.department  || '',
      );
    }
  }, [accessChecked, hasAccess, sharedFilters.fy, currentTabFilters.directorate, currentTabFilters.department, fetchOptions]);

  // ── Fetch dashboard data ──
  const fetchData = useCallback(async (
    tab: string,
    tabFilt: Record<string, string>,
    shared: { fy: string; period: string },
    isRefresh = false,
  ) => {
    if (isRefresh) setRefreshing(true); else { setLoading(true); setData(null); }
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('tab', tab);
      if (shared.fy)     params.set('fy',     shared.fy);
      if (shared.period) params.set('period', shared.period);
      for (const k of ['directorate', 'department', 'costCentre', 'poStatus']) {
        if (tabFilt[k]) params.set(k, tabFilt[k]);
      }
      if (tabFilt.excludeProject === 'true') params.set('excludeProject', 'true');
      const res = await accessService.get(`/finance/summary?${params}`);
      setData(res.data);
      setLoadedAt(new Date().toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (accessChecked && hasAccess) fetchData(activeTab, currentTabFilters, sharedFilters);
  }, [activeTab, currentTabFilters, sharedFilters, fetchData, accessChecked, hasAccess]);

  // ── Dropdown options ──
  const buildOpts = (list: string[] = [], allLabel = 'All'): IComboBoxOption[] =>
    [{ key: '', text: allLabel }, ...list.map(v => ({ key: v, text: v }))];

  // FY dropdown — prefer filterOpts (available ~1s after mount, before data arrives)
  const fyOptions: IDropdownOption[] = useMemo(() => {
    const years = filterOpts?.financialYears || data?.filters?.financialYears || [];
    return years.map(y => ({ key: String(y), text: 'FY ' + String(y) }));
  }, [filterOpts?.financialYears, data?.filters?.financialYears]);

  // Hierarchy dropdowns — prefer filterOpts so they cascade in real time
  // (directorate → department → cost centre) without waiting for a full data reload
  const directorateOptions = useMemo(
    () => buildOpts(filterOpts?.directorates || data?.filters?.directorates, 'All Directorates'),
    [filterOpts?.directorates, data?.filters?.directorates],
  );
  const departmentOptions = useMemo(
    () => buildOpts(filterOpts?.departments || data?.filters?.departments, 'All Departments'),
    [filterOpts?.departments, data?.filters?.departments],
  );
  const costCentreOptions = useMemo(
    () => buildOpts(filterOpts?.costCentres || data?.filters?.costCentres, 'All Cost Centres'),
    [filterOpts?.costCentres, data?.filters?.costCentres],
  );
  // PO Status has no fast-path (only in /summary); falls back to data as before
  const poStatusOptions = useMemo(
    () => buildOpts(data?.filters?.poStatuses, 'All Statuses'),
    [data?.filters?.poStatuses],
  );

  const activeTabConfig = TAB_CONFIG.find(t => t.id === activeTab)!;
  const tabColor = activeTabConfig.color;
  const tabPalette = activeTabConfig.palette;
  const tabFilterKeys = TAB_FILTERS[activeTab] || [];

  const kpis = data?.kpis || {};
  const charts = data?.charts || {};
  const table = data?.table || [];

  // renderKpiValue replaced by <KpiHeroValue> module-level component above

  // ── Drill-down bar click (overview / variance / PO rollup chart) ──
  const handleRollupBarClick = (entry: any) => {
    if (!entry || !entry.Name) return;
    const level = charts.rollupLevel;
    if (level === 'Directorate') setTabFilter('directorate', entry.Name);
    else if (level === 'Department') setTabFilter('department', entry.Name);
    // At CostCentre level, clicking does nothing (already deepest)
  };

  // ══════════════════════════════════════════════════════════
  // Access control gate
  // ══════════════════════════════════════════════════════════
  if (!accessChecked) {
    return <div className={s.page}><div className={s.centerWrap}><Spinner size={SpinnerSize.large} label="Checking access..." /></div></div>;
  }
  if (!hasAccess) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => nav('/dashboards')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: p.primary, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <CaretLeft size={10} /> Dashboards
            </button>
          </div>
          <div style={{ textAlign: 'center', padding: '80px 24px' }}>
            <Text variant="xLarge" style={{ fontWeight: 700, color: p.text, display: 'block', marginBottom: 12 }}>Access Required</Text>
            <Text variant="medium" style={{ color: p.text2 }}>This is a governed dashboard. Contact your administrator to request access to Finance data.</Text>
          </div>
        </div>
      </div>
    );
  }
  if (loading && !data) {
    return <div className={s.page}><div className={s.centerWrap}><Spinner size={SpinnerSize.large} label="Loading Finance Dashboard..." /></div></div>;
  }

  // ══════════════════════════════════════════════════════════
  // Breadcrumb — shows the current hierarchy drill level
  // ══════════════════════════════════════════════════════════
  const renderBreadcrumb = () => {
    const { directorate, department, costCentre } = currentTabFilters;
    if (!directorate && !department && !costCentre) return null;
    return (
      <div className={s.breadcrumbRow}>
        <HouseLine size={12} />
        <button className={s.breadcrumbChip} onClick={() => { clearTabFilter('directorate'); }}>All Directorates</button>
        {directorate && (<><CaretRight size={10} color="#999" /><button className={s.breadcrumbChip} onClick={() => { clearTabFilter('department'); clearTabFilter('costCentre'); }}>{directorate}</button></>)}
        {department  && (<><CaretRight size={10} color="#999" /><button className={s.breadcrumbChip} onClick={() => clearTabFilter('costCentre')}>{department}</button></>)}
        {costCentre  && (<><CaretRight size={10} color="#999" /><span className={s.breadcrumbCurrent}>{costCentre}</span></>)}
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════
  // Render
  // ══════════════════════════════════════════════════════════
  return (
    <div className={s.page}>
      {/* --tc CSS variable propagates the active tab's accent colour to all
          chart/KPI cards without individual style props on every element. */}
      <div className={s.container} style={{ '--tc': tabColor } as React.CSSProperties}>

        {/* Back nav */}
        <div style={{ marginBottom: 10 }}>
          <button onClick={() => nav('/dashboards')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 13, fontWeight: 600, color: p.primary, fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <CaretLeft size={10} /> Dashboards
          </button>
        </div>

        {/* Page header */}
        <div className={s.headerRow}>
          <Text variant="xxLarge" className={s.title}>Finance & Contract Expenditure</Text>
          <Text variant="medium" className={s.subtitle}>
            Ledger actuals, budget variance, project spend and purchase commitments — drillable by directorate, department and cost centre.
          </Text>
          <div className={s.timestamp}>
            <Clock size={12} color={p.text3} />
            {loadedAt ? `Last updated: ${loadedAt}` : 'Loading…'}
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab, currentTabFilters, sharedFilters, true)} disabled={refreshing}>
              <ArrowsClockwise size={12} className={refreshing ? s.spinning : undefined} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Scope banner */}
        <AccessSummaryBanner kind="finance" />

        {/* ── Tab strip with icon + label ── */}
        <div className={s.tabStrip}>
          {TAB_CONFIG.map(tab => {
            const TIcon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                className={s.tab}
                style={isActive ? { color: tab.color, background: tab.color + '14', fontWeight: 700 } : undefined}
                onClick={() => setActiveTab(tab.id)}
              >
                <TIcon size={14} weight={isActive ? 'fill' : 'regular'} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Filter bar ── */}
        {tabFilterKeys.length > 0 && (
          <div className={s.filterSection}>
            <div className={s.filterBar}>
              <span className={s.filterLabel}><Funnel size={11} /> Filters</span>

              {/* FY — persists across tabs */}
              {tabFilterKeys.includes('fy') && fyOptions.length > 0 && (
                <Dropdown
                  placeholder="FY"
                  selectedKey={sharedFilters.fy}
                  options={fyOptions}
                  onChange={(_, opt) => setFY((opt?.key as string) || '')}
                  styles={{ dropdown: { minWidth: 115, border: '1px solid #D1D5DB', borderRadius: 6 }, title: { fontSize: 12, fontWeight: 500, color: '#232323', borderRadius: 6 } }}
                />
              )}

              {/* Period — Overview only, YTD bound */}
              {tabFilterKeys.includes('period') && (
                <Dropdown
                  placeholder="Full Year"
                  selectedKey={sharedFilters.period || ''}
                  options={PERIOD_OPTIONS}
                  onChange={(_, opt) => setPeriod((opt?.key as string) || '')}
                  styles={{ dropdown: { minWidth: 138, border: '1px solid #D1D5DB', borderRadius: 6 }, title: { fontSize: 12, fontWeight: 500, color: sharedFilters.period ? GREEN : '#232323', borderRadius: 6 } }}
                />
              )}

              {tabFilterKeys.includes('directorate') && directorateOptions.length > 1 && (
                <ComboBox placeholder="All Directorates" selectedKey={currentTabFilters.directorate || ''} options={directorateOptions} autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                  onChange={(_, opt) => { if (opt) setTabFilter('directorate', (opt.key as string) || ''); }}
                  onPendingValueChanged={(opt, _i, v) => { if (!v && !opt) setTabFilter('directorate', ''); }}
                  styles={{ root: { minWidth: 200 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #D1D5DB', borderRadius: 6 } }} />
              )}
              {tabFilterKeys.includes('department') && departmentOptions.length > 1 && (
                <ComboBox placeholder="All Departments" selectedKey={currentTabFilters.department || ''} options={departmentOptions} autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                  onChange={(_, opt) => { if (opt) setTabFilter('department', (opt.key as string) || ''); }}
                  onPendingValueChanged={(opt, _i, v) => { if (!v && !opt) setTabFilter('department', ''); }}
                  styles={{ root: { minWidth: 220 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #D1D5DB', borderRadius: 6 } }} />
              )}
              {tabFilterKeys.includes('costCentre') && costCentreOptions.length > 1 && (
                <ComboBox placeholder="All Cost Centres" selectedKey={currentTabFilters.costCentre || ''} options={costCentreOptions} autoComplete="on" allowFreeInput useComboBoxAsMenuWidth
                  onChange={(_, opt) => { if (opt) setTabFilter('costCentre', (opt.key as string) || ''); }}
                  onPendingValueChanged={(opt, _i, v) => { if (!v && !opt) setTabFilter('costCentre', ''); }}
                  styles={{ root: { minWidth: 240 }, input: { fontSize: 12, fontWeight: 500, color: '#232323' }, container: { border: '1px solid #D1D5DB', borderRadius: 6 } }} />
              )}
              {tabFilterKeys.includes('poStatus') && poStatusOptions.length > 1 && (
                <Dropdown placeholder="All Statuses" selectedKey={currentTabFilters.poStatus || ''} options={poStatusOptions as any}
                  onChange={(_, opt) => setTabFilter('poStatus', (opt?.key as string) || '')}
                  styles={{ dropdown: { minWidth: 148, border: '1px solid #D1D5DB', borderRadius: 6 }, title: { fontSize: 12, fontWeight: 500, color: '#232323', borderRadius: 6 } }} />
              )}
              {tabFilterKeys.includes('excludeProject') && (
                <TooltipHost content="When ON, excludes any GL/PO postings tagged to a project (ProjectNumber set). When OFF, the dashboard shows all postings — the true council-wide view.">
                  <Toggle
                    label="Operating only"
                    checked={currentTabFilters.excludeProject === 'true'}
                    onChange={(_, c) => setTabFilter('excludeProject', c ? 'true' : '')}
                    inlineLabel
                    styles={{ root: { marginBottom: 0 }, label: { fontSize: 12, fontWeight: 500, color: '#484848' } }}
                  />
                </TooltipHost>
              )}
            </div>

            {/* ── Active filter chips with individual × clear ── */}
            {activeFilterChips.length > 0 && (
              <div className={s.chipRow}>
                {activeFilterChips.map(chip => (
                  <span key={chip.key} className={s.chip}>
                    <span className={s.chipLabel}>{chip.label}</span>
                    <button className={s.chipClear} onClick={chip.onClear} title="Remove filter" aria-label={`Clear ${chip.label}`}>×</button>
                  </span>
                ))}
                {activeFilterChips.length > 1 && (
                  <button className={s.clearAllLink} onClick={clearAllFilters}>Clear all</button>
                )}
              </div>
            )}

            {/* Hierarchy drill breadcrumb */}
            {renderBreadcrumb()}
          </div>
        )}

        {/* Error */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error} className={s.error}>
            {error}
            <button className={s.refreshBtn} onClick={() => fetchData(activeTab, currentTabFilters, sharedFilters)} style={{ marginLeft: 12 }}>
              <ArrowsClockwise size={12} /> Retry
            </button>
          </MessageBar>
        )}

        {/* ── Context strip + KPI band (all tabs except Overview) ── */}
        {data && activeTab !== 'overview' && (
          <>
            {/* Eyebrow strip — mirrors fo-context in FinancialOverview, tabColor applied inline */}
            <div
              className={s.tabContext}
              style={{
                background: `linear-gradient(to right, ${tabColor}0b, transparent 70%)`,
                borderLeft: `3px solid ${tabColor}`,
              }}
            >
              {tabFilterKeys.includes('fy') && (
                <>
                  <span style={{ color: tabColor, letterSpacing: '0.05em' }}>FY {sharedFilters.fy}</span>
                  <span className={s.tabCtxSep}>·</span>
                </>
              )}
              <span>{currentTabFilters.directorate || 'All Directorates'}</span>
              {currentTabFilters.department && (
                <>
                  <span className={s.tabCtxSep}>›</span>
                  <span>{currentTabFilters.department}</span>
                </>
              )}
              {currentTabFilters.costCentre && (
                <>
                  <span className={s.tabCtxSep}>›</span>
                  <span>{currentTabFilters.costCentre}</span>
                </>
              )}
              {currentTabFilters.poStatus && (
                <>
                  <span className={s.tabCtxSep}>·</span>
                  <span className={s.tabCtxPill}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: tabColor, display: 'inline-block' }} />
                    {currentTabFilters.poStatus}
                  </span>
                </>
              )}
              {currentTabFilters.excludeProject === 'true' && (
                <>
                  <span className={s.tabCtxSep}>·</span>
                  <span className={s.tabCtxPill}>Operating only</span>
                </>
              )}
            </div>

            {/* KPI cards — no icon badges; top accent stripe matches chart cards */}
            {Object.keys(kpis).length > 0 && (
              <div className={s.kpiRow}>
                {Object.entries(kpis).map(([key, kpi]) => (
                  <div key={key} className={s.kpiCard}>
                    <div className={s.kpiLabel}>{kpi.label}</div>
                    <KpiHeroValue kpi={kpi} />
                    {kpi.detail && <div className={s.kpiHint} style={{ marginTop: 8 }}>{kpi.detail}</div>}
                    {kpi.hint   && <div className={s.kpiHint} style={{ marginTop: 4 }}>{kpi.hint}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ══ TAB 1: FINANCIAL OVERVIEW — bespoke editorial layout ══ */}
        {activeTab === 'overview' && data && (
          <FinancialOverview
            kpis={kpis as any}
            charts={charts as any}
            tabColor={tabColor}
            tabPalette={tabPalette}
            fy={sharedFilters.fy}
            period={sharedFilters.period}
            periodLabel={PERIOD_OPTIONS.find(o => o.key === sharedFilters.period)?.text || ''}
            excludeProject={currentTabFilters.excludeProject === 'true'}
            onDrillBar={handleRollupBarClick}
          />
        )}

        {/* ══ TAB 2: VARIANCE ══ */}
        {activeTab === 'variance' && data && (
          <>
            {/* ── Section: Performance ── */}
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Performance</span>
              <div className={s.sectionRule} />
              <span className={s.sectionHint}>FY {sharedFilters.fy} · Actuals vs Budget</span>
            </div>

            <div className={s.chartsRow}>
              {charts.monthlyTrend && charts.monthlyTrend.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>Monthly Actuals vs Budget</span>
                    <div className={s.chartCardMeta}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: tabColor }} />Actuals
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 3, borderRadius: 1, background: '#9CA3AF', marginTop: 3 }} />Budget
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={charts.monthlyTrend} margin={{ top: 6, right: 18, left: 6, bottom: 6 }}>
                      <defs>
                        <linearGradient id={`var-actuals-fill-${activeTab}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={tabColor} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={tabColor} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => v?.slice(0, 3)} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} width={56} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Area type="monotone" dataKey="Actuals" stroke="none" fill={`url(#var-actuals-fill-${activeTab})`} />
                      <Line type="monotone" dataKey="Budget"  name="Budget"  stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                      <Line type="monotone" dataKey="Actuals" name="Actuals" stroke={tabColor} strokeWidth={2.5} dot={{ r: 3, fill: tabColor, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.varianceByRollup && charts.varianceByRollup.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>Variance by {charts.rollupLevel || 'Directorate'} (Top 10)</span>
                    <div className={s.chartCardMeta}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#9CA3AF' }} />Budget
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: tabColor }} />Actuals
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={charts.varianceByRollup} layout="vertical" margin={{ top: 5, right: 80, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis type="category" dataKey="Name" width={160} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 24)} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Bar dataKey="Budget"  name="Budget"  fill="#9CA3AF" barSize={10} radius={[0, 3, 3, 0]} />
                      <Bar dataKey="Actuals" name="Actuals" fill={tabColor} barSize={10} radius={[0, 3, 3, 0]} onClick={(e: any) => handleRollupBarClick({ Name: e?.Name })} cursor="pointer">
                        <LabelList content={BarValueLabel} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ── Section: Variance detail table ── */}
            {table.length > 0 && (
              <>
                <div className={s.sectionHead}>
                  <span className={s.sectionTitle}>Variance Detail</span>
                  <div className={s.sectionRule} />
                  <span className={s.sectionHint}>by {charts.rollupLevel || 'Directorate'}</span>
                </div>
                <div className={s.tableCard}>
                  <DataTable
                    rows={table}
                    exportFileName={`variance-${(charts.rollupLevel || 'directorate').toLowerCase()}`}
                    defaultSort={{ key: 'Actuals', dir: 'desc' }}
                    columns={[
                      {
                        key: 'Name',
                        label: charts.rollupLevel || 'Directorate',
                        minWidth: 220,
                        render: (row: any) => (
                          <button
                            onClick={() => openBreakdown(row.Name, charts.rollupLevel || 'Directorate')}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2A9D8F', fontWeight: 600, fontSize: 12, textAlign: 'left', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}
                          >
                            {row.Name}
                          </button>
                        ),
                      } as Column<any>,
                      { key: 'Budget',      label: 'Budget',     format: 'currency', showBar: true },
                      { key: 'Actuals',     label: 'Actuals',    format: 'currency', showBar: true },
                      { key: 'Variance',    label: 'Variance',   format: 'currency' },
                      {
                        key: 'VariancePct',
                        label: 'Variance %',
                        align: 'right',
                        render: (row: any) => {
                          const pct = Number(row.VariancePct) || 0;
                          const col = pct < -10 ? RED : pct > 10 ? GREEN : '#484848';
                          return <span style={{ color: col, fontWeight: 600 }}>{pct.toFixed(1)}%</span>;
                        },
                      },
                    ]}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* ══ TAB 3: HIERARCHY (was Cost Centres) ══ */}
        {activeTab === 'costcentres' && data && (
          <>
            {/* ── Section: Cost centre spend ── */}
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Cost Centre Spend</span>
              <div className={s.sectionRule} />
              <span className={s.sectionHint}>FY {sharedFilters.fy} · Top 15 by spend</span>
            </div>

            <div className={s.chartsRowFull}>
              {charts.topCostCentres && charts.topCostCentres.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>Top 15 Cost Centres by Spend</span>
                    <div className={s.chartCardMeta}>
                      <span>Spend $</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart data={charts.topCostCentres} layout="vertical" margin={{ top: 5, right: 80, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis type="category" dataKey="CostCentre" width={200} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 30)} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Bar dataKey="Spend" radius={[0, 4, 4, 0]} barSize={18}>
                        <LabelList content={BarValueLabel} />
                        {charts.topCostCentres.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ── Section: Detail table ── */}
            {table.length > 0 && (
              <>
                <div className={s.sectionHead}>
                  <span className={s.sectionTitle}>Detail</span>
                  <div className={s.sectionRule} />
                  <span className={s.sectionHint}>Cost Centres with Directorate / Department</span>
                </div>
                <div className={s.tableCard}>
                  <DataTable
                    rows={table}
                    exportFileName="cost-centres"
                    defaultSort={{ key: 'TotalSpend', dir: 'desc' }}
                    columns={[
                      { key: 'Directorate',   label: 'Directorate' },
                      { key: 'Department',    label: 'Department' },
                      { key: 'CostCentre',    label: 'Cost Centre', minWidth: 220 },
                      { key: 'TotalSpend',    label: 'Spend',        format: 'currency', showBar: true },
                      { key: 'PctOfTotal',    label: '% of Total',   format: 'percent' },
                      { key: 'Transactions',  label: 'Txns',         format: 'number' },
                    ] as Column<any>[]}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* ══ TAB 4: PROJECTS ══ */}
        {activeTab === 'projects' && data && (
          <>
            {/* ── Section: Project spend ── */}
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Project Spend</span>
              <div className={s.sectionRule} />
              <span className={s.sectionHint}>FY {sharedFilters.fy} · Project Ledger (PL)</span>
            </div>
            <p style={{ fontSize: 11, color: '#9CA3AF', margin: '-4px 0 14px', fontStyle: 'italic' }}>
              Project budget is attributed to directorates via each project's dominant cost centre (by posted actuals).
              Projects with no actuals yet appear in the Budget Unattributed tile above.
            </p>

            <div className={s.chartsRow}>
              {charts.topProjects && charts.topProjects.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>Top 15 Projects</span>
                    <div className={s.chartCardMeta}>
                      <span>By total spend</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={charts.topProjects} layout="vertical" margin={{ top: 5, right: 80, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis type="category" dataKey="Project" width={200} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 30)} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Bar dataKey="Spend" radius={[0, 4, 4, 0]} barSize={16}>
                        <LabelList content={BarValueLabel} />
                        {charts.topProjects.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.monthlyTrend && charts.monthlyTrend.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>Monthly Project Spend vs Budget</span>
                    <div className={s.chartCardMeta}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: tabColor }} />Spend
                      </span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <i style={{ display: 'inline-block', width: 10, height: 3, borderRadius: 1, background: '#9CA3AF', marginTop: 3 }} />Budget
                      </span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <ComposedChart data={charts.monthlyTrend} margin={{ top: 6, right: 18, left: 6, bottom: 6 }}>
                      <defs>
                        <linearGradient id="proj-spend-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={tabColor} stopOpacity={0.18} />
                          <stop offset="100%" stopColor={tabColor} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                      <XAxis dataKey="Month" tick={{ fontSize: 11, fill: '#484848' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} width={56} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Area type="monotone" dataKey="Spend" stroke="none" fill="url(#proj-spend-fill)" />
                      <Line type="monotone" dataKey="Budget" name="Budget" stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                      <Line type="monotone" dataKey="Spend"  name="Spend"  stroke={tabColor} strokeWidth={2.5} dot={{ r: 3, fill: tabColor, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ── Section: Project budget vs actuals table ── */}
            {table.length > 0 && (
              <>
                <div className={s.sectionHead}>
                  <span className={s.sectionTitle}>Project Detail</span>
                  <div className={s.sectionRule} />
                  <span className={s.sectionHint}>Budget vs Actuals · {table.length} projects</span>
                </div>
                <div className={s.tableCard}>
                  <DataTable
                    rows={table}
                    exportFileName="projects-budget-vs-actuals"
                    defaultSort={{ key: 'Actuals', dir: 'desc' }}
                    columns={[
                      { key: 'Project',     label: 'Project',      minWidth: 260 },
                      { key: 'Directorate', label: 'Directorate' },
                      { key: 'Budget',      label: 'Budget',       format: 'currency', showBar: true },
                      { key: 'Actuals',     label: 'Actuals',      format: 'currency', showBar: true },
                      { key: 'Variance',    label: 'Variance ($)',  format: 'currency' },
                      {
                        key: 'PctUsed',
                        label: '% Used',
                        align: 'right' as const,
                        render: (row: any) => {
                          const pct = Number(row.PctUsed);
                          if (isNaN(pct) || row.PctUsed === null) return <span style={{ color: '#9CA3AF' }}>—</span>;
                          const col = pct > 100 ? RED : pct > 85 ? '#E76F51' : GREEN;
                          return <span style={{ color: col, fontWeight: 700 }}>{pct.toFixed(1)}%</span>;
                        },
                      },
                    ] as Column<any>[]}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* ══ TAB 5: PURCHASE ORDERS ══ */}
        {activeTab === 'purchaseorders' && data && (
          <>
            {/* ── Section: Overview charts ── */}
            <div className={s.sectionHead}>
              <span className={s.sectionTitle}>Purchase Orders</span>
              <div className={s.sectionRule} />
              <span className={s.sectionHint}>
                {currentTabFilters.directorate || 'All directorates'}
                {currentTabFilters.poStatus ? ` · ${currentTabFilters.poStatus}` : ''}
              </span>
            </div>

            <div className={s.chartsRow}>
              {charts.poStatus && charts.poStatus.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>PO Status Breakdown</span>
                    <div className={s.chartCardMeta}>
                      {charts.poStatus.map((item: any, i: number) => (
                        <span key={item.Status} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <i style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: tabPalette[i % tabPalette.length] }} />
                          {item.Status}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie data={charts.poStatus} dataKey="Amount" nameKey="Status" cx="50%" cy="50%" innerRadius={60} outerRadius={120} paddingAngle={2}>
                        {charts.poStatus.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {charts.topRollup && charts.topRollup.length > 0 && (
                <div className={s.chartCard}>
                  <div className={s.chartCardHead}>
                    <span className={s.chartCardTitle}>PO Value by {charts.rollupLevel || 'Directorate'}</span>
                    <div className={s.chartCardMeta}>
                      <span>Click to drill down</span>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={charts.topRollup} layout="vertical" margin={{ top: 5, right: 80, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" horizontal={false} />
                      <XAxis type="number" tickFormatter={fmtAxis} tick={{ fontSize: 11, fill: '#747474' }} axisLine={{ stroke: '#E4E4E4' }} tickLine={{ stroke: '#E4E4E4' }} />
                      <YAxis type="category" dataKey="Name" width={180} tick={{ fontSize: 10, fill: '#484848' }} tickFormatter={(v: string) => truncLabel(v, 26)} axisLine={false} tickLine={false} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => fmtCurrency(v)} labelStyle={{ color: '#484848', fontWeight: 600, marginBottom: 4 }} />
                      <Bar dataKey="Amount" radius={[0, 4, 4, 0]} barSize={16} onClick={handleRollupBarClick as any} cursor="pointer">
                        <LabelList content={BarValueLabel} />
                        {charts.topRollup.map((_: any, i: number) => <Cell key={i} fill={tabPalette[i % tabPalette.length]} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* ── Section: PO list ── */}
            {table.length > 0 && (
              <>
                <div className={s.sectionHead}>
                  <span className={s.sectionTitle}>Purchase Order List</span>
                  <div className={s.sectionRule} />
                  <span className={s.sectionHint}>
                    {table.length.toLocaleString('en-AU')} records · use column filters to narrow
                  </span>
                </div>
                <div className={s.tableCard}>
                  <DataTable
                    rows={table}
                    exportFileName="purchase-orders"
                    defaultSort={{ key: 'Ordered', dir: 'desc' }}
                    columns={[
                      { key: 'PONumber',     label: 'PO Number',   minWidth: 110 },
                      { key: 'Supplier',     label: 'Supplier',    minWidth: 220 },
                      { key: 'Status',       label: 'Status',      minWidth: 110 },
                      { key: 'Directorate',  label: 'Directorate' },
                      { key: 'Department',   label: 'Department' },
                      { key: 'CostCentre',   label: 'Cost Centre', minWidth: 180 },
                      { key: 'Description',  label: 'Description', minWidth: 280 },
                      { key: 'Ordered',      label: 'Ordered',     format: 'currency', showBar: true },
                      { key: 'Billed',       label: 'Billed',      format: 'currency' },
                    ] as Column<any>[]}
                  />
                </div>
              </>
            )}
          </>
        )}

        {/* No-data fallback */}
        {data && !loading && !error && Object.keys(kpis).length === 0 && Object.keys(charts).length === 0 && table.length === 0 && (
          <div className={s.noData}>No data available for this tab.</div>
        )}

        {/* ── Account Breakdown Drawer (two-level: account list → journal lines) ── */}
        <Panel
          isOpen={!!breakdown}
          onDismiss={() => {
            setBreakdown(null); setBreakdownData(null);
            setAccountDrill(null); setAccountLines(null);
          }}
          type={PanelType.medium}
          headerText={accountDrill ? accountDrill.name : (breakdown?.name || '')}
          isLightDismiss
          styles={{
            header:     { paddingTop: 20, paddingLeft: 24 },
            headerText: { fontSize: 18, fontWeight: 700, color: '#232323', letterSpacing: '-0.02em' },
            content:    { paddingTop: 0, paddingLeft: 24, paddingRight: 24 },
          }}
        >
          {/* ── Level 2: individual GL journal lines for one account ── */}
          {accountDrill && (
            <>
              <button
                onClick={() => { setAccountDrill(null); setAccountLines(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2A9D8F', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', padding: '0 0 18px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                ← {breakdown?.name}
              </button>
              {accountLinesLoading && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
                  <Spinner size={SpinnerSize.large} label="Loading journal lines…" />
                </div>
              )}
              {!accountLinesLoading && accountLines && (
                <>
                  {accountLinesCapped && (
                    <div style={{ fontSize: 11, color: '#E76F51', marginBottom: 8, fontStyle: 'italic' }}>
                      Showing first 500 lines · use Export to download all
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12, fontStyle: 'italic' }}>
                    FY {sharedFilters.fy} · {accountLines.length} lines · most recent first
                  </div>
                  {accountLines.length === 0
                    ? <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 32, textAlign: 'center' as const }}>No journal lines found</div>
                    : (
                      <DataTable
                        rows={accountLines}
                        exportFileName={`journal-lines-${(accountDrill.name).replace(/\s+/g, '-').toLowerCase()}`}
                        defaultSort={{ key: 'PostingDate', dir: 'desc' }}
                        columns={[
                          { key: 'PostingDate',     label: 'Date',        minWidth: 88  },
                          { key: 'LineDescription', label: 'Description', minWidth: 200 },
                          { key: 'Source',          label: 'Source',      minWidth: 75  },
                          { key: 'Reference',       label: 'Reference',   minWidth: 100 },
                          {
                            key: 'Amount',
                            label: 'Amount',
                            align: 'right' as const,
                            render: (row: any) => {
                              const amt = Number(row.Amount);
                              const col = amt < 0 ? GREEN : '#232323';
                              return <span style={{ color: col, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(Math.abs(amt))}</span>;
                            },
                          },
                        ] as Column<any>[]}
                      />
                    )
                  }
                </>
              )}
            </>
          )}

          {/* ── Level 1: account breakdown list ── */}
          {!accountDrill && breakdownLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
              <Spinner size={SpinnerSize.large} label="Loading account breakdown…" />
            </div>
          )}
          {!accountDrill && !breakdownLoading && breakdownData && (() => {
            const { summary, rows } = breakdownData;
            const varColor = summary.variance >= 0 ? GREEN : RED;
            return (
              <>
                {/* Summary strip */}
                <div style={{ display: 'flex', gap: 28, padding: '16px 0 20px', borderBottom: '1px solid #E4E4E4', marginBottom: 20, flexWrap: 'wrap' as const }}>
                  {([
                    { label: 'Budget',   value: summary.budget,   color: '#484848' },
                    { label: 'Actuals',  value: summary.actuals,  color: '#232323' },
                    { label: 'Variance', value: summary.variance, color: varColor  },
                  ] as { label: string; value: number; color: string }[]).map(item => (
                    <div key={item.label}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: '#9CA3AF', marginBottom: 4 }}>{item.label}</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: item.color, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(item.value)}</div>
                    </div>
                  ))}
                </div>
                {/* Context note */}
                <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 12, fontStyle: 'italic' }}>
                  GL expense accounts (5*) · FY {sharedFilters.fy} · {breakdown?.level} view · click an account name to see journal lines
                </div>
                {rows.length === 0
                  ? <div style={{ color: '#9CA3AF', fontSize: 13, paddingTop: 32, textAlign: 'center' as const }}>No account data found for this selection</div>
                  : (
                    <DataTable
                      rows={rows}
                      exportFileName={`account-breakdown-${(breakdown?.name || 'detail').replace(/\s+/g, '-').toLowerCase()}`}
                      defaultSort={{ key: 'Actuals', dir: 'desc' }}
                      columns={[
                        {
                          key: 'AccountName',
                          label: 'Account',
                          minWidth: 180,
                          render: (row: any) => (
                            <button
                              onClick={() => openAccountDrill(row.AccountNumber, row.AccountName)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2A9D8F', fontWeight: 600, fontSize: 12, textAlign: 'left', padding: 0, textDecoration: 'underline', fontFamily: 'inherit' }}
                            >
                              {row.AccountName}
                            </button>
                          ),
                        },
                        { key: 'AccountNumber', label: 'Code',     minWidth: 55  },
                        { key: 'Budget',        label: 'Budget',   format: 'currency' },
                        { key: 'Actuals',       label: 'Actuals',  format: 'currency', showBar: true },
                        { key: 'Variance',      label: 'Variance', format: 'currency' },
                        {
                          key: 'PctUsed',
                          label: '% Used',
                          align: 'right' as const,
                          render: (row: any) => {
                            const pct = Number(row.PctUsed);
                            if (isNaN(pct) || row.PctUsed === null) return <span style={{ color: '#9CA3AF' }}>—</span>;
                            const col = pct > 100 ? RED : pct > 85 ? '#E76F51' : GREEN;
                            return <span style={{ color: col, fontWeight: 700 }}>{pct.toFixed(1)}%</span>;
                          },
                        },
                      ] as Column<any>[]}
                    />
                  )
                }
              </>
            );
          })()}
        </Panel>
      </div>
    </div>
  );
}
